import { useRef, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { avatar, avatars, avatarSrc, isUploaded, setAvatar } from "../game/avatar";
import { faNum } from "../game/cards";
import { chatOff, setChatOff } from "../game/chat";
import { cycleSpeed, speed as speedSig, speedName } from "../game/speed";
import { PlaceSettings } from "./Place";
import { CountryFlag } from "./CountryFlag";
import { authHref, useRegistered } from "./Account";
import { ask, notify } from "./Dialog";
import { BlockedList } from "./PlayerSheet";

/**
 * «پروفایلِ من» — هرچه درباره‌ی خودم است، یک‌جا.
 *
 * تا امروز پخش بود: چهره و نام در بالای هاب، کشور و شهر در یک ردیفِ دیگر،
 * سرعت و چت در نوارِ میز، و حساب در صفحه‌ی جدا. هیچ‌کدام غلط نبودند، ولی
 * بازیکنی که می‌خواست «عکسم را عوض کنم» باید می‌دانست کجا را بگردد.
 *
 * نام و ایمیل و رمز هر سه **روی سرور** اند و نه در این مرورگر. نام دکمه‌ی
 * «ذخیره»ی خودش را دارد و نه ذخیره‌ی خودکار: سقفِ هفت‌روزه‌ی سرور یعنی هر
 * حرفی که تایپ می‌شود نباید یک تغییرِ نام باشد.
 *
 * چیزی که این‌جا **نیست**: خودِ بازی. این صفحه هیچ‌چیزی را نمی‌فروشد و
 * هیچ میزی را باز نمی‌کند؛ فقط تنظیم است.
 */
export function Profile({ onBack }: { onBack: () => void }) {
  const me = useSignal(avatar);
  const registered = useRegistered();
  const username = useSignal(client.username);
  const off = useSignal(chatOff);
  useSignal(speedSig);           // نامِ سرعت با تغییرش تازه شود

  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← خانه</button>
        <b style={{ flex: 1 }}>پروفایل من</b>
      </div>

      <FaceAndName me={me} registered={registered} />

      {/* ---- تنظیمات ---- */}
      <div className="plate stack">
        <div className="muted">تنظیمات</div>
        <button className="btn ghost" onClick={cycleSpeed}>سرعت بازی — {speedName()}</button>
        <label className="row" style={{ gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={off} onChange={(e) => setChatOff(e.target.checked)} />
          <span>چت میز خاموش باشد</span>
        </label>
        <PlaceSettings />
      </div>

      {/* ---- بلاک‌شده‌ها: روی حساب‌اند، پس این‌جا همان فهرستی است که اپ هم می‌بیند ---- */}
      <div className="plate stack">
        <div className="muted">بلاک‌شده‌ها</div>
        <BlockedList />
      </div>

      {registered ? (
        <>
          <EmailSection />
          <PasswordSection />
          <div className="plate stack">
            <div className="muted">حساب</div>
            <div>وارد شده‌ای با <b>{username}</b></div>
            <button
              className="btn ghost danger"
              onClick={() => void (async () => {
                if (!(await ask({ title: "از حساب بیرون بروم؟", body: "سکه و قلم‌هایت سر جایشان می‌مانند.", ok: "بیرون برو", danger: true }))) return;
                const err = await client.logout();
                notify(err ? "نشد؛ دوباره امتحان کن" : "بیرون آمدی");
              })()}
            >
              خروج از حساب
            </button>
          </div>
        </>
      ) : (
        <div className="plate stack">
          <div className="muted">حساب</div>
          <div className="muted">مهمانی. بازی و اتاق دوستان حساب نمی‌خواهد؛ برای میز ورودی‌دار، نگه‌داشتن سکه‌ها روی هر دستگاه و پشتیبانی، حساب لازم است.</div>
          <a className="btn" href={authHref("login")}>ورود / ثبت‌نام</a>
        </div>
      )}
    </div>
  );
}

/** چهره و نام — همان دو چیزی که هم‌بازی سرِ میز می‌بیند. */
function FaceAndName({ me, registered }: { me: string; registered: boolean }) {
  const serverName = useSignal(client.playerName);
  const country = useSignal(client.country);
  const showPlace = useSignal(client.showPlace);
  const [name, setName] = useState(serverName);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const uploaded = isUploaded(me);
  const dirty = name.trim() !== "" && name.trim() !== serverName;

  async function save() {
    if (!dirty || busy) return;
    setBusy(true);
    const r = await client.setName(name.trim());
    setBusy(false);
    if (r.error) { setMsg(nameError(r.error, r.until)); return; }
    setMsg("");
    notify("نامت عوض شد");
  }

  async function pick(file: File | undefined) {
    if (!file || busy) return;
    setBusy(true);
    const r = await client.uploadAvatar(file);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";   // همان فایل دوباره انتخاب‌شدنی بماند
    if (r.error) {
      notify(r.error === "too_large" ? "عکس بزرگ است — کمتر از ۶ مگابایت"
        : r.error === "image_only" ? "این فایل عکس نیست"
        : r.error === "too_many" ? "کمی بعد دوباره امتحان کن"
        : r.error === "unauthorized" ? "اول وارد شو"
        : "نشد؛ دوباره امتحان کن");
      return;
    }
    setAvatar(r.url!);
    client.reconnectForIdentity();
    notify("عکست عوض شد");
  }

  async function dropPhoto() {
    if (busy) return;
    setBusy(true);
    const err = await client.clearAvatar();
    setBusy(false);
    if (err) { notify("نشد؛ دوباره امتحان کن"); return; }
    setAvatar("player");
    client.reconnectForIdentity();
    notify("عکس برداشته شد");
  }

  return (
    <div className="plate stack">
      <div className="muted">چهره و نامت سر میز</div>
      <div className="row" style={{ gap: 10 }}>
        <span className="cflag-host">
          <button className="facebtn" onClick={() => setPicking(!picking)} aria-label="انتخاب چهره" title="چهره">
            <img src={avatarSrc(me)} alt="" width={56} height={56} />
          </button>
          <CountryFlag code={country} hidden={!showPlace} />
        </span>
        <input
          className="code"
          style={{ letterSpacing: "normal", textAlign: "right", direction: "rtl" }}
          value={name}
          placeholder="مهمان"
          maxLength={16}
          aria-label="نامت سر میز"
          onChange={(e) => { setName(e.target.value); setMsg(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
        />
        <button className="btn" style={{ inlineSize: "auto", paddingInline: 16 }} disabled={!dirty || busy} onClick={() => void save()}>
          ذخیره
        </button>
      </div>
      {msg && <div className="muted" style={{ color: "var(--danger, #d96a5e)" }}>{msg}</div>}
      <div className="muted">نام هر هفته یک بار عوض می‌شود. هم‌بازی‌ها با همین نام تو را می‌شناسند.</div>

      {/* عکسِ خودت. فقط برای حسابِ ثبت‌شده: عکس روی سرور می‌مانَد و باید
          صاحبی داشته باشد که بشود پاسخ‌گویش کرد. */}
      {registered ? (
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <button className="btn ghost" disabled={busy} style={{ inlineSize: "auto", paddingInline: 16 }}
                  onClick={() => fileRef.current?.click()}>
            {uploaded ? "عکس دیگری بگذار" : "عکس خودت را بگذار"}
          </button>
          {uploaded && (
            <button className="btn ghost danger" disabled={busy} style={{ inlineSize: "auto", paddingInline: 16 }}
                    onClick={() => void dropPhoto()}>
              بردار
            </button>
          )}
        </div>
      ) : (
        <div className="muted">برای گذاشتن عکس خودت باید ثبت‌نام کنی.</div>
      )}
      {picking && (
        <div className="faces">
          {avatars.map((a) => (
            <button key={a.key} className={`face ${a.key === me ? "on" : ""}`} aria-label={a.fa}
                    onClick={() => { setAvatar(a.key); setPicking(false); client.reconnectForIdentity(); }}>
              <img src={avatarSrc(a.key)} alt="" width={64} height={64} />
              <span>{a.fa}</span>
            </button>
          ))}
        </div>
      )}
      <div className="muted">
        چهره و نام را هم‌بازی‌ها و تماشاچی‌ها می‌بینند. عکس روی سرور مربع و کوچک می‌شود و اطلاعات همراهش — مثل مکان عکس‌برداری — دور ریخته می‌شود. عکس ناجور را پشتیبانی برمی‌دارد و می‌تواند حساب را هم محدود کند.
      </div>
    </div>
  );
}

/**
 * ایمیل — دو گام، چون کد به نشانیِ **تازه** می‌رود.
 *
 * تا کدِ نشانیِ تازه نیامده، ایمیلِ قبلی سرِ جایش است: نشانیِ تازه ممکن است
 * غلط تایپ شده باشد، و آن وقت راهِ بازگشت به حساب گم می‌شد.
 */
function EmailSection() {
  const current = useSignal(client.email);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function request() {
    if (busy) return;
    setBusy(true);
    const err = await client.changeEmail(email.trim(), password);
    setBusy(false);
    if (err) { setMsg(emailError(err)); return; }
    setMsg("");
    setSent(email.trim());
    setPassword("");
  }

  async function confirm() {
    if (busy) return;
    setBusy(true);
    const err = await client.verifyEmail(code.trim());
    setBusy(false);
    if (err) { setMsg(emailError(err)); return; }
    setMsg(""); setSent(""); setOpen(false); setEmail(""); setCode("");
    notify("ایمیلت عوض شد");
  }

  return (
    <div className="plate stack">
      <div className="muted">ایمیل</div>
      <div dir="ltr" style={{ textAlign: "right" }}>{current || "—"}</div>
      {!open && <button className="btn ghost" onClick={() => setOpen(true)}>تغییر ایمیل</button>}
      {open && !sent && (
        <>
          <input className="code" style={{ letterSpacing: "normal" }} dir="ltr" type="email" inputMode="email"
                 placeholder="ایمیل تازه" value={email} onChange={(e) => { setEmail(e.target.value); setMsg(""); }} />
          <input className="code" style={{ letterSpacing: "normal" }} dir="ltr" type="password" autoComplete="current-password"
                 placeholder="رمز فعلی" value={password} onChange={(e) => { setPassword(e.target.value); setMsg(""); }} />
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={busy || !email.trim() || !password} onClick={() => void request()}>کد را بفرست</button>
            <button className="btn ghost" onClick={() => { setOpen(false); setMsg(""); }}>بی‌خیال</button>
          </div>
        </>
      )}
      {sent && (
        <>
          <div className="muted">کد شش‌رقمی به <b dir="ltr">{sent}</b> رفت. تا کد را وارد نکنی، ایمیلت عوض نمی‌شود.</div>
          <input className="code" dir="ltr" inputMode="numeric" maxLength={6} placeholder="۶ رقم"
                 value={code} onChange={(e) => { setCode(e.target.value); setMsg(""); }} />
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" disabled={busy || code.trim().length < 6} onClick={() => void confirm()}>تأیید</button>
            <button className="btn ghost" onClick={() => { setSent(""); setCode(""); setMsg(""); }}>ایمیل دیگری</button>
          </div>
        </>
      )}
      {msg && <div className="muted" style={{ color: "var(--danger, #d96a5e)" }}>{msg}</div>}
    </div>
  );
}

/** رمز — همین‌جا و نه در صفحه‌ی دیگر؛ رمزِ قبلی خواسته نمی‌شود چون توکنِ این
 *  دستگاه خودش هویت است. ولی تکرارش خواسته می‌شود، مثلِ ثبت‌نام و بازنشانی: یک
 *  غلطِ تایپی حساب را پشتِ رمزی می‌گذاشت که کسی نمی‌داند (کاربر، ۱ اکتبر ۲۰۲۶). */
function PasswordSection() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function save() {
    if (busy || !pw) return;
    if (pw2 !== pw) { setMsg("رمزها با هم یکی نیستند"); return; }
    setBusy(true);
    const err = await client.setPassword(pw);
    setBusy(false);
    if (err) { setMsg(err === "weak_password" ? "رمز باید دست‌کم ۱۰ حرف باشد، با حرف بزرگ و کوچک لاتین، رقم و نشانه" : "نشد؛ دوباره امتحان کن"); return; }
    setMsg(""); setPw(""); setPw2("");
    notify("رمزت عوض شد");
  }

  return (
    <div className="plate stack">
      <div className="muted">رمز</div>
      <input className="code" style={{ letterSpacing: "normal" }} dir="ltr" type="password" autoComplete="new-password"
             aria-label="رمز تازه" placeholder="رمز تازه" value={pw} onChange={(e) => { setPw(e.target.value); setMsg(""); }} />
      <input className="code" style={{ letterSpacing: "normal" }} dir="ltr" type="password" autoComplete="new-password"
             aria-label="تکرار رمز تازه" placeholder="تکرار رمز تازه" value={pw2}
             onChange={(e) => { setPw2(e.target.value); setMsg(""); }}
             onKeyDown={(e) => { if (e.key === "Enter") void save(); }} />
      <button className="btn ghost" disabled={busy || !pw || !pw2} onClick={() => void save()}>تغییر رمز</button>
      {msg && <div className="muted" style={{ color: "var(--danger, #d96a5e)" }}>{msg}</div>}
    </div>
  );
}

function nameError(e: string, until?: string): string {
  switch (e) {
    case "bad_name": return "نام نمی‌تواند خالی باشد";
    case "rude_name": return "این نام مناسب نیست";
    case "too_soon": return "نامت را به‌تازگی عوض کرده‌ای — " + whenAgain(until);
    case "too_many": return "کمی بعد دوباره امتحان کن";
    case "unauthorized": return "اول وارد شو";
    default: return "نشد؛ دوباره امتحان کن";
  }
}

/** «کِی می‌شود» — بی این، بازیکن هر روز برمی‌گردد تا تصادفاً روزش برسد. */
function whenAgain(until?: string): string {
  const ms = until ? Date.parse(until) - Date.now() : NaN;
  if (!Number.isFinite(ms) || ms <= 0) return "کمی بعد دوباره امتحان کن";
  const days = Math.ceil(ms / 86_400_000);
  return days > 1 ? faNum(days) + " روز دیگر می‌شود" : "فردا می‌شود";
}

function emailError(e: string): string {
  switch (e) {
    case "bad_email": return "این ایمیل درست نیست";
    case "bad_password": return "رمز درست نیست";
    case "email_taken": return "این ایمیل متعلق به حساب دیگری است";
    case "same_email": return "همین حالا هم همین است";
    case "not_registered": return "اول ثبت‌نام کن";
    case "bad_code": return "کد درست نیست";
    case "code_expired": return "کد منقضی شده — دوباره بخواه";
    case "too_many": return "کمی بعد دوباره امتحان کن";
    case "mail": return "ایمیل فرستاده نشد؛ کمی بعد دوباره امتحان کن";
    default: return "نشد؛ دوباره امتحان کن";
  }
}
